import React, { useContext, useEffect, useState } from 'react';
import { Col, Row, Spinner } from 'react-bootstrap';
import { AppContext } from '../../App';
import LabDataLayout, { useLabDataFilters } from '../../components/LabData/LabDataLayout';
import LabDataKpiCard from '../../components/LabData/LabDataKpiCard';
import LabDataWarningsBanner from '../../components/LabData/LabDataWarningsBanner';
import SpendOverTimeChart from '../../components/LabData/SpendOverTimeChart';
import SpendBySupplierTable from '../../components/LabData/SpendBySupplierTable';
import {
  getSpendByPeriod,
  getSpendBySupplier,
  getSpendSummary,
} from '../../clients/lab_data_client';
import { formatNisAmount } from '../../utils/labData';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo } from '../../config_and_helpers/logger';

const LOG_CONTEXT = 'LabDataSpendPage';

/**
 * Loads spend summary, period series, and supplier breakdown for the URL filters.
 */
const LabDataSpendContent = () => {
  const { token } = useContext(AppContext);
  const filters = useLabDataFilters();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    if (!token || !filters) return;

    let cancelled = false;
    setLoading(true);
    logInfo(LOG_CONTEXT, 'load spend data', filters);

    const options = {
      from: filters.from,
      to: filters.to,
      budgetLineId: filters.budgetLineId,
      projectId: filters.projectId,
      supplierId: filters.supplierId,
    };

    Promise.all([
      getSpendSummary(token, options),
      getSpendByPeriod(token, options),
      getSpendBySupplier(token, options),
    ]).then(([summaryRes, periodRes, supplierRes]) => {
      if (cancelled) return;
      setLoading(false);

      if (!summaryRes.success) {
        showToast(
          summaryRes.messages?.[0] || 'Failed to load spend summary',
          'error',
          'top-right',
          5000
        );
        return;
      }

      setSummary(summaryRes.data);
      setWarnings(summaryRes.data?.warnings ?? []);
      setPeriods(periodRes.success ? periodRes.data?.data?.periods ?? [] : []);
      setSuppliers(
        supplierRes.success ? supplierRes.data?.data?.suppliers ?? [] : []
      );
    });

    return () => {
      cancelled = true;
    };
  }, [token, filters]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading spend overview</span>
        </Spinner>
      </div>
    );
  }

  const data = summary?.data ?? {};

  return (
    <>
      <LabDataWarningsBanner warnings={warnings} />
      <Row className="g-3 lab-data-kpi-grid">
        <Col xs={12} sm={4}>
          <LabDataKpiCard
            label="Total spend"
            value={formatNisAmount(data.total_nis)}
            subtitle={`${data.order_count ?? 0} orders · ${data.line_count ?? 0} lines`}
          />
        </Col>
        <Col xs={12} sm={4}>
          <LabDataKpiCard
            label="Period"
            value={`${filters?.from ?? ''} → ${filters?.to ?? ''}`}
            subtitle={summary?.currency ? `Currency: ${summary.currency}` : 'NIS'}
          />
        </Col>
        <Col xs={12} sm={4}>
          <LabDataKpiCard
            label="Generated"
            value={summary?.generated_at ? new Date(summary.generated_at).toLocaleString() : '—'}
          />
        </Col>
      </Row>
      <SpendOverTimeChart periods={periods} />
      <SpendBySupplierTable
        suppliers={suppliers}
        dateFrom={filters?.from}
        dateTo={filters?.to}
      />
    </>
  );
};

/**
 * Lab Data landing page: spend KPIs, chart, and supplier table.
 */
const LabDataSpendPage = () => (
  <LabDataLayout title="Lab data — Spend overview" showSpendFilters>
    <LabDataSpendContent />
  </LabDataLayout>
);

export default LabDataSpendPage;
